import { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { HookExecutionTester } from '@/components/test/HookExecutionTester';
import { HookAnalyzer } from '@/components/test/HookAnalyzer';
import { usePerformanceMetrics } from '@/hooks/usePerformanceMetrics';
import { createHookExecutionContext, useHookConsistencyCheck } from '@/utils/hookTestUtils';

/**
 * Hook Testing Dashboard
 * 
 * A comprehensive testing environment for validating React hooks execution
 * patterns and identifying potential issues with component rendering.
 */
export default function HookTestingDashboard() {
  // Use our performance metrics hook to measure component performance
  const { measureRender, markInteractive } = usePerformanceMetrics('HookTestingDashboard');
  const endRenderMeasure = measureRender();
  
  // Select which tool to display
  const [activeTab, setActiveTab] = useState<'tester' | 'analyzer' | 'examples'>('tester');
  
  // Hook execution context for examples
  const executionContext = useRef(createHookExecutionContext());
  
  // Used to track hook consistency - only for demonstration
  const {
    recordHook,
    hasInconsistencies,
    getRenderCount
  } = useHookConsistencyCheck('HookTestingDashboard');
  
  // Record hooks for demonstration
  recordHook('useState:activeTab');
  recordHook('useRef:executionContext');
  recordHook('useHookConsistencyCheck');
  
  // Mark component as interactive after initial setup
  markInteractive();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Hook Testing Dashboard | Meduler</title>
        <meta name="description" content="Testing and validating React hooks execution patterns" />
      </Helmet>
      
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            React Hooks Testing Dashboard
          </h1>
          <p className="mt-3 text-xl text-gray-500">
            Tools for validating hook execution order and identifying potential issues
          </p>
          <p className="mt-2 text-sm text-gray-400">
            Render count: {getRenderCount()} | Inconsistencies detected: {hasInconsistencies() ? 'Yes' : 'No'}
          </p>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 mb-8">
          <button
            onClick={() => setActiveTab('tester')}
            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
              activeTab === 'tester'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Hook Execution Tester
          </button>
          <button
            onClick={() => setActiveTab('analyzer')}
            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
              activeTab === 'analyzer'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Hook Analyzer
          </button>
          <button
            onClick={() => setActiveTab('examples')}
            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
              activeTab === 'examples'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Code Examples
          </button>
        </div>
        
        {/* Tab Content */}
        {activeTab === 'tester' && (
          <div>
            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
              <h2 className="text-2xl font-bold mb-4">Hook Execution Tester</h2>
              <p className="mb-6 text-gray-600">
                This tool allows you to test how hooks execute in different states. Toggle the view modes and
                loading states to see how hooks maintain consistent execution order.
              </p>
              
              <HookExecutionTester />
            </div>
          </div>
        )}
        
        {activeTab === 'analyzer' && (
          <div>
            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
              <h2 className="text-2xl font-bold mb-4">Hook Execution Analyzer</h2>
              <p className="mb-6 text-gray-600">
                A more advanced tool that visualizes hook execution order including effect callbacks and 
                cleanup functions. This helps identify potential issues with hook dependencies.
              </p>
              
              <HookAnalyzer />
            </div>
          </div>
        )}
        
        {activeTab === 'examples' && (
          <div>
            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
              <h2 className="text-2xl font-bold mb-4">Hook Implementation Examples</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border rounded-md p-4 bg-red-50">
                  <h3 className="text-lg font-semibold mb-2 text-red-700">❌ Problematic Pattern</h3>
                  <pre className="bg-gray-800 text-white p-4 rounded overflow-auto text-sm">
{`function ProblematicComponent() {
  // State declarations
  const [isLoading, setIsLoading] = useState(true);
  
  // Early return creates inconsistent hook execution
  if (isLoading) {
    return <LoadingSpinner />;
  }
  
  // This hook won't execute when isLoading is true!
  useEffect(() => {
    fetchData();
  }, []);
  
  // More hooks here will also be skipped
  
  return <MainContent />;
}`}
                  </pre>
                  
                  <div className="mt-4 text-sm text-red-700">
                    <h4 className="font-medium">Issues:</h4>
                    <ul className="list-disc pl-5 space-y-1 mt-1">
                      <li>Early return creates inconsistent hook execution paths</li>
                      <li>Hooks after the condition will be skipped on some renders</li>
                      <li>Can lead to the "React Hook cannot be called inside a callback" error</li>
                      <li>Makes debugging extremely difficult</li>
                    </ul>
                  </div>
                </div>
                
                <div className="border rounded-md p-4 bg-green-50">
                  <h3 className="text-lg font-semibold mb-2 text-green-700">✅ Correct Pattern</h3>
                  <pre className="bg-gray-800 text-white p-4 rounded overflow-auto text-sm">
{`function OptimizedComponent() {
  // Performance measurement
  const { measureRender } = usePerformanceMetrics('Component');
  const endRenderMeasure = measureRender();
  
  // All hooks declared unconditionally
  const [isLoading, setIsLoading] = useState(true);
  
  // This hook executes on EVERY render
  useEffect(() => {
    fetchData();
  }, []);
  
  // Conditional rendering instead of early return
  return (
    <>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <MainContent />
      )}
      {endRenderMeasure()}
    </>
  );
}`}
                  </pre>
                  
                  <div className="mt-4 text-sm text-green-700">
                    <h4 className="font-medium">Benefits:</h4>
                    <ul className="list-disc pl-5 space-y-1 mt-1">
                      <li>All hooks execute in the same order on every render</li>
                      <li>Performance measurement is non-intrusive</li>
                      <li>Component tree structure remains consistent</li>
                      <li>State changes don't affect hook execution paths</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 border-t pt-6">
                <h3 className="text-xl font-bold mb-4">More Advanced Patterns</h3>
                
                <div className="grid grid-cols-1 gap-6">
                  <div className="border rounded-md p-4 bg-blue-50">
                    <h3 className="text-lg font-semibold mb-2">Suspense Component Pattern</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-red-600 mb-2">❌ Problematic</h4>
                        <pre className="bg-gray-800 text-white p-3 rounded overflow-auto text-sm">
{`function BadSuspensePattern() {
  const [showFeature, setShowFeature] = useState(false);
  
  return (
    <div>
      {/* Conditional Suspense creates inconsistent trees */}
      {showFeature && (
        <Suspense fallback={<Spinner />}>
          <LazyFeature />
        </Suspense>
      )}
    </div>
  );
}`}
                        </pre>
                      </div>
                      
                      <div>
                        <h4 className="font-medium text-green-600 mb-2">✅ Corrected</h4>
                        <pre className="bg-gray-800 text-white p-3 rounded overflow-auto text-sm">
{`function GoodSuspensePattern() {
  const [showFeature, setShowFeature] = useState(false);
  
  return (
    <div>
      {/* Consistent Suspense tree */}
      <Suspense fallback={<Spinner />}>
        {showFeature ? <LazyFeature /> : null}
      </Suspense>
    </div>
  );
}`}
                        </pre>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow-md p-6 mt-8">
          <h2 className="text-xl font-bold mb-4">Test Your Components</h2>
          <p className="text-gray-600 mb-4">
            To test your own components for hook execution consistency, use the{' '}
            <code className="bg-gray-100 px-1 py-0.5 rounded text-pink-600">useHookConsistencyCheck</code>{' '}
            and{' '}
            <code className="bg-gray-100 px-1 py-0.5 rounded text-pink-600">useHookExecutionTracker</code>{' '}
            hooks from our utility library:
          </p>
          
          <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto">
{`import { useHookConsistencyCheck } from '@/utils/hookTestUtils';

function YourComponent() {
  // Add this hook at the top of your component
  const { recordHook, hasInconsistencies } = useHookConsistencyCheck('YourComponent');
  
  // Record each hook as it's used
  recordHook('useState:example');
  const [state, setState] = useState(initialState);
  
  recordHook('useEffect:example');
  useEffect(() => {
    // Your effect code
  }, []);
  
  // The rest of your component...
  return (
    <div>
      {/* Show warning if inconsistencies detected */}
      {hasInconsistencies() && (
        <div className="text-red-500">Hook execution inconsistency detected!</div>
      )}
      {/* The rest of your UI */}
    </div>
  );
}`}
          </pre>
        </div>
      </div>
      
      {/* End render measurement at the end of the component */}
      {endRenderMeasure()}
    </div>
  );
}
